import { useParams, Link } from "react-router-dom";
import {
  User, Building2, Clock, ArrowLeft, FileText, MapPin, ExternalLink,
} from "lucide-react";
import Header from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { useKnowledgeStory } from "@/hooks/useKnowledge";

const CATEGORY_ICON  = { person: User, company: Building2 };
const CATEGORY_LABEL = { person: "نجاح شخصيات", company: "نجاح شركات" };

export default function KnowledgeStoryPage() {
  const { id: idOrSlug } = useParams();
  const { story, isLoading, error } = useKnowledgeStory(idOrSlug);

  if (isLoading) return <Shell><Loading /></Shell>;
  if (error || !story) return <Shell><NotFound ident={idOrSlug} /></Shell>;

  const Icon = CATEGORY_ICON[story.category] ?? User;

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-surface-200 bg-white">
          <div className="pointer-events-none absolute inset-0 fursa-grid-bg opacity-40" aria-hidden />
          <div className="relative mx-auto max-w-3xl px-4 pb-8 pt-8 sm:px-6 sm:pt-12 lg:px-8">
            <Link
              to="/knowledge"
              data-testid="knowledge-back"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-500 transition-colors hover:text-intel-900"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              العودة إلى المكتبة
            </Link>

            <div className="mt-5 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-200 bg-surface-50 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
                <Icon className="h-3 w-3 text-intel-700" strokeWidth={2} aria-hidden />
                {CATEGORY_LABEL[story.category]}
              </span>
            </div>

            <p
              data-testid="knowledge-subject"
              className="mt-4 text-[13px] font-semibold uppercase tracking-widest text-ink-500"
            >
              {story.subjectName}
            </p>
            <h1 className="arabic-headline mt-2 text-[26px] font-bold leading-[1.18] tracking-tightest text-ink-900 text-balance sm:text-[36px] sm:leading-[1.12] lg:text-[44px]">
              {story.title}
            </h1>
            {story.intro && (
              <p className="arabic-body mt-4 text-[15px] leading-[1.75] text-ink-700 text-pretty sm:mt-5 sm:text-[16px]">
                {story.intro}
              </p>
            )}

            {/* Sprint 28b — single cover image (URL-only, hosted externally
                today; object-storage upload lands in Sprint 28c). Falls back
                to nothing when the URL is missing so old stories keep working. */}
            {story.coverImageUrl && (
              <figure data-testid="story-cover" className="mt-6 overflow-hidden rounded-xl border border-surface-200 bg-white">
                <img
                  src={story.coverImageUrl}
                  alt={story.coverImageAlt || story.title}
                  loading="lazy"
                  className="block h-auto max-h-[420px] w-full object-cover"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
                {story.coverImageSource && (
                  <figcaption dir="ltr" className="border-t border-surface-100 bg-surface-50 px-3 py-1.5 text-[11px] text-ink-500">
                    Source · {story.coverImageSource}
                  </figcaption>
                )}
              </figure>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-ink-500">
              {story.publishedDisplay && <span>{story.publishedDisplay}</span>}
              <span aria-hidden className="text-surface-300">·</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                <span dir="ltr" className="num font-mono tabular-nums">
                  {story.readTimeMinutes}
                </span> دقيقة قراءة
              </span>
              {story.country && (
                <>
                  <span aria-hidden className="text-surface-300">·</span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                    {story.country}
                  </span>
                </>
              )}
              {story.industry && (
                <>
                  <span aria-hidden className="text-surface-300">·</span>
                  <span>{story.industry}</span>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <article className="space-y-10">
            <Section eyebrow="ما الذي حدث؟"     body={story.whatHappened}   testId="knowledge-what-happened" />
            <Section eyebrow="لماذا نجح؟"        body={story.whySucceeded}   testId="knowledge-why-succeeded" />
            <Section eyebrow="ماذا نتعلّم؟"       body={story.lessonsLearned} testId="knowledge-lessons-learned" />
          </article>

          {/* Sources */}
          {(story.sources || []).length > 0 && (
            <aside
              data-testid="knowledge-sources"
              className="mt-12 rounded-xl border border-surface-200 bg-white p-5 shadow-card"
            >
              <div className="flex items-center gap-2 border-b border-surface-100 pb-3">
                <FileText className="h-3.5 w-3.5 text-intel-800" strokeWidth={2} aria-hidden />
                <p className="text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
                  المصادر
                </p>
              </div>
              <ul className="mt-3 space-y-2.5">
                {story.sources.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px]">
                    <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-none text-ink-500" strokeWidth={1.75} aria-hidden />
                    <div className="min-w-0 flex-1">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ink-900 underline decoration-surface-300 underline-offset-4 hover:decoration-intel-500 hover:text-intel-900"
                        >
                          {s.name || s.url}
                        </a>
                      ) : (
                        <span className="text-ink-900">{s.name}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

const Section = ({ eyebrow, body, testId }) => (
  <div data-testid={testId}>
    <h2 className="arabic-headline text-[19px] font-semibold tracking-tight text-ink-900 text-balance sm:text-[21px]">
      {eyebrow}
    </h2>
    <p className="arabic-body mt-3 text-[15px] leading-[1.9] text-ink-700 text-pretty">
      {body}
    </p>
  </div>
);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-surface-50 flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

const Loading = () => (
  <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
    <div className="fursa-skeleton mb-6 h-4 w-24 rounded" />
    <div className="fursa-skeleton mb-3 h-10 w-3/4 rounded" />
    <div className="fursa-skeleton mb-8 h-6 w-2/3 rounded" />
    <div className="space-y-3">
      <div className="fursa-skeleton h-4 rounded" />
      <div className="fursa-skeleton h-4 w-5/6 rounded" />
      <div className="fursa-skeleton h-4 w-4/6 rounded" />
    </div>
  </div>
);

const NotFound = ({ ident }) => (
  <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6 lg:px-8">
    <h1 className="text-2xl font-semibold text-ink-900">لم نعثر على هذه القصّة</h1>
    <p className="mt-3 text-sm text-ink-500">
      المُعرِّف <code className="rounded bg-surface-100 px-1.5 py-0.5 font-mono text-xs">{ident}</code> غير موجود أو لم يُنشر بعد.
    </p>
    <Link
      to="/knowledge"
      className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-fursa-800"
    >
      <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
      العودة إلى المكتبة
    </Link>
  </div>
);
